import type { FC } from 'react';
import { useState } from 'react';
import { Popper } from '@mui/base';
import { Autocomplete, TextField } from '@mui/material';

import { useDebounce } from 'src/hooks';
import type { FollowUpOwner } from 'src/types/followUp';
import type { FollowUpAction } from 'src/types/myFollowUp';
import { usePotentialFollowUpOwners } from 'src/components/myFollowUp/MyFollowUpPage/CreateMyFollowUpModal/Sections/SelectOwner/usePotentialFollowUpOwners';
import useFollowUpDetail, {
  FollowUpDetailProvider,
} from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpDetailModal/FollowUpDetailContext';

interface ChangeOwnerFollowUpDetailModalProps {
  onClose: () => void;
  anchorEl: HTMLElement | null;
  followUpAction: FollowUpAction;
  orgUnitIds?: Array<number>;
  enable?: boolean;
}

const ChangeOwnerFollowUpDetailModal: FC<
  ChangeOwnerFollowUpDetailModalProps
> = ({ onClose, anchorEl, followUpAction, orgUnitIds, enable = true }) => {
  const [searchString, setSearchString] = useState<string>('');
  const debouncedOwnerSearch = useDebounce(searchString, 500);
  const { data } = usePotentialFollowUpOwners({
    search: debouncedOwnerSearch,
    orgUnitIds: orgUnitIds,
  });
  const { onSave } = useFollowUpDetail();
  const onChange = (value: FollowUpOwner) => {
    onSave({ followUpOwner: value.id }).finally(() => onClose());
  };

  return (
    <FollowUpDetailProvider actionId={followUpAction.id} enable={enable}>
      <Popper
        anchorEl={anchorEl}
        placement="bottom-end"
        style={{
          width: '100%',
          padding: '0.25rem 1rem',
        }}
        open={!!anchorEl}
        disablePortal
      >
        {data && (
          <Autocomplete
            renderInput={(params) => <TextField {...params} />}
            getOptionLabel={(option) => option.fullName}
            options={data.results}
            inputValue={searchString}
            onInputChange={(_, newInputValue) => {
              setSearchString(newInputValue);
            }}
            open={true}
            onChange={(_, value) => onChange(value as FollowUpOwner)}
            ListboxProps={{ style: { maxHeight: '12rem' } }}
          />
        )}
      </Popper>
    </FollowUpDetailProvider>
  );
};

export default ChangeOwnerFollowUpDetailModal;
